// «HAY UNA VERSIÓN NUEVA DE LA APP» — EL AVISO DE ACTUALIZAR LA APP DEL MÓVIL (6-oct-2026)
//
// El dueño (6-oct): «que la web detecte desde qué versión de app de Play Store o App Store están entrando [...] y si la
// versión que están usando es menor a la publicada [...] que les salga un aviso de actualizar y les redirija a la tienda
// correspondiente; ya que al ser web app, no van a actualizar a no ser que entren a la tienda y lo vean».
//
// MONTADO UNA SOLA VEZ, EN LA RAÍZ (App.jsx), así que sirve a las DOS apps, que cargan esta misma web: la del equipo
// (es.biango.dental, también en la pantalla de entrar) y la del paciente (es.biango.pacientes, el portal). La app se
// distingue por su identificador (App.getInfo().id), y el servidor contesta por ESA app y ESA plataforma.
//
// QUÉ HACE, Y SOLO DENTRO DE LA APP NATIVA (Capacitor). En el navegador (también el del móvil) no hace NADA, ni una
// petición: ahí no hay nada que actualizar.
//   1. lee la versión instalada (App.getInfo: en Android el nombre de la versión, p. ej. «2.3»; en el iPhone, la que se ve
//      en la App Store). Una app vieja sin ese complemento no la sabe decir: entonces no se molesta a nadie;
//   2. pregunta a /api/apps-tienda la publicada, la mínima y el enlace (routes/appsTiendaPublico.js). «No sé» = nada;
//   3. si la instalada es MENOR (comparando número a número: 1.10 es mayor que 1.9; utils/versionTienda), avisa:
//        · «Actualizar» abre la ficha de su tienda (Google Play en Android, App Store en el iPhone). Es un enlace a otro
//          dominio, y Capacitor abre FUERA de la app todo lo que no es biango.app: sale la tienda, no una web dentro;
//        · «Ahora no» lo deja para el día siguiente (o para cuando salga otra versión más nueva);
//        · por debajo de la MÍNIMA no hay «Ahora no»: el aviso tapa la app hasta que se actualice.
//   4. al volver a la app tras unas horas en segundo plano, vuelve a mirar.
import { useEffect, useRef, useState } from 'react'
import { App as AppNativa } from '@capacitor/app'
import { enLaAppNativa } from '../utils/verDocumento'
import { decidirAviso, leerAhoraNo, guardarAhoraNo, pospuestoHoy, diaLocal } from '../utils/versionTienda'
import { t } from '../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

// Si la app se queda abierta en segundo plano (días, a veces), al volver a ella se vuelve a mirar, como mucho cada 6 horas.
const VOLVER_A_MIRAR_MS = 6 * 60 * 60 * 1000

// El almacén del móvil, si se deja (en algunos modos privados leerlo lanza): sin él, «Ahora no» vale hasta cerrar la app.
function almacenDe(w) {
  try { return w && w.localStorage ? w.localStorage : null } catch (_) { return null }
}

// LO QUE TOCA ENSEÑAR, o null. Nunca lanza: cualquier cosa que no se sepa es no molestar.
async function calcularAviso(w) {
  try {
    if (!enLaAppNativa(w)) return null
    const plataforma = typeof w.Capacitor.getPlatform === 'function' ? w.Capacitor.getPlatform() : ''
    if (plataforma !== 'ios' && plataforma !== 'android') return null
    const info = await AppNativa.getInfo()
    const app = info && typeof info.id === 'string' ? info.id : ''
    const instalada = info && info.version != null ? String(info.version) : ''
    if (!app || !instalada) return null
    if (typeof w.fetch !== 'function') return null
    const r = await w.fetch('/api/apps-tienda?app=' + encodeURIComponent(app) + '&plataforma=' + plataforma,
      { headers: { accept: 'application/json' } })
    if (!r || !r.ok) return null
    const d = await r.json()
    const aviso = decidirAviso({ instalada, plataforma, publicada: d && d.publicada, minima: d && d.minima, enlace: d && d.enlace })
    if (!aviso) return null
    if (!aviso.obligatorio && pospuestoHoy(leerAhoraNo(almacenDe(w)), { app, publicada: aviso.publicada, hoy: diaLocal() })) return null
    return { ...aviso, app }
  } catch (_) {
    return null
  }
}

export default function AvisoActualizarApp() {
  const [aviso, setAviso] = useState(null)
  const accion = useRef(null)

  useEffect(() => {
    const w = typeof window !== 'undefined' ? window : null
    if (!w || !enLaAppNativa(w)) return undefined   // en el navegador: nada, ni una petición
    let vivo = true
    let ultima = 0
    const mirar = () => {
      ultima = Date.now()
      calcularAviso(w).then((a) => { if (vivo) setAviso(a) })
    }
    mirar()
    const doc = w.document
    const alVolver = () => {
      try { if (doc.visibilityState === 'visible' && Date.now() - ultima >= VOLVER_A_MIRAR_MS) mirar() } catch (_) { /* sin documento */ }
    }
    if (doc && typeof doc.addEventListener === 'function') doc.addEventListener('visibilitychange', alVolver)
    return () => {
      vivo = false
      if (doc && typeof doc.removeEventListener === 'function') doc.removeEventListener('visibilitychange', alVolver)
    }
  }, [])

  // «Ahora no»: hasta el día siguiente, para esta versión. El obligatorio no tiene «Ahora no».
  const ahoraNo = () => {
    if (!aviso || aviso.obligatorio) return
    const w = typeof window !== 'undefined' ? window : null
    guardarAhoraNo(almacenDe(w), { app: aviso.app, publicada: aviso.publicada, dia: diaLocal() })
    setAviso(null)
  }

  // Al salir, el foco va a «Actualizar» (quien usa un lector de pantalla oye el aviso); Escape es «Ahora no».
  useEffect(() => {
    if (!aviso) return undefined
    const el = accion.current
    if (el && typeof el.focus === 'function') { try { el.focus() } catch (_) { /* sin foco */ } }
    if (aviso.obligatorio) return undefined
    const w = typeof window !== 'undefined' ? window : null
    const doc = w && w.document
    if (!doc || typeof doc.addEventListener !== 'function') return undefined
    const tecla = (e) => { if (e && e.key === 'Escape') ahoraNo() }
    doc.addEventListener('keydown', tecla)
    return () => { doc.removeEventListener('keydown', tecla) }
  }, [aviso])

  if (!aviso) return null
  const obligatorio = aviso.obligatorio === true
  const titulo = obligatorio ? t('components.AvisoActualizarApp.tienes_que_actualizar_la_app', 'Tienes que actualizar la app') : t('components.AvisoActualizarApp.hay_una_version_nueva_de', 'Hay una versión nueva de la app')
  const texto = obligatorio
    ? t('components.AvisoActualizarApp.esta_version_de_la_app', 'Esta versión de la app ({{instalada}}) ya no se puede usar. Actualízala {{enTienda}} para seguir: es un momento y no se pierde nada.', { instalada: aviso.instalada, enTienda: aviso.enTienda })
    : t('components.AvisoActualizarApp.tienes_la_version_y_ya', 'Tienes la versión {{instalada}} y {{enTienda}} ya está la {{publicada}}. Actualízala para tener todo lo último: es un momento y no se pierde nada.', { instalada: aviso.instalada, enTienda: aviso.enTienda, publicada: aviso.publicada })
  return (
    <div className="modal-safe" data-aviso-actualizar-app={obligatorio ? 'obligatorio' : 'nuevo'}
      style={{
        position: 'fixed', inset: 0, zIndex: obligatorio ? 100000 : 99985,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: obligatorio ? 'var(--surface-2)' : 'rgba(15, 23, 42, 0.55)',
      }}>
      <div role={obligatorio ? 'alertdialog' : 'dialog'} aria-modal="true"
        aria-labelledby="aviso-actualizar-app-titulo" aria-describedby="aviso-actualizar-app-texto"
        style={{
          width: '100%', maxWidth: '400px', boxSizing: 'border-box', textAlign: 'center',
          background: 'var(--surface)', color: 'var(--text-primary)',
          border: '1px solid var(--border)', borderRadius: '18px', padding: '24px 20px 16px',
          boxShadow: '0 18px 50px rgba(0, 0, 0, 0.28)',
        }}>
        <div aria-hidden="true" style={{
          width: '52px', height: '52px', margin: '0 auto 12px', borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'color-mix(in srgb, var(--primary) 14%, transparent)', color: 'var(--primary)',
        }}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
            strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5" /><path d="m5 12 7-7 7 7" /></svg>
        </div>
        <h2 id="aviso-actualizar-app-titulo" style={{ margin: '0 0 8px', fontSize: '19px', fontWeight: 800, lineHeight: 1.3 }}>
          {titulo}
        </h2>
        <p id="aviso-actualizar-app-texto" style={{ margin: '0 0 18px', fontSize: '15px', lineHeight: 1.5, color: 'var(--text-secondary)' }}>
          {texto}
        </p>
        {/* Un ENLACE a la ficha de la tienda, y en otra ventana: dentro de la app, Capacitor abre fuera todo lo que no es
            biango.app, así que se abre la tienda del móvil (y no una web dentro de la app). */}
        <a ref={accion} href={aviso.enlace} target="_blank" rel="noopener noreferrer" data-actualizar-app="1"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '48px', borderRadius: '12px',
            background: 'var(--primary)', color: 'var(--sobre-primary, #fff)', fontSize: '16px', fontWeight: 800,
            textDecoration: 'none',
          }}>
          {t('components.AvisoActualizarApp.actualizar', 'Actualizar')}
        </a>
        {!obligatorio && (
          <button type="button" onClick={ahoraNo} data-ahora-no="1"
            style={{
              marginTop: '6px', width: '100%', minHeight: '44px', border: 'none', background: 'transparent',
              color: 'var(--text-secondary)', fontSize: '15px', fontWeight: 700, cursor: 'pointer',
            }}>
            {t('components.AvisoActualizarApp.ahora_no', 'Ahora no')}
          </button>
        )}
      </div>
    </div>
  )
}
